/**
 * DL-60478: explicit route -> access-control scope overrides.
 *
 * The route-level `<AccessControl>` in `src/app.tsx` normally derives its scope from
 * `location.pathname` (see `useScope`), so for most routes the URL *is* the scope. That is
 * fine while no two route paths share a prefix, because `checkAccess` matches a scope
 * against a page with `scope.startsWith(page) || page.startsWith(scope)` -- a two-way prefix
 * match that is load-bearing for nested routes ('style/workflow' must satisfy 'style') and
 * therefore cannot be narrowed to an exact match without a repo-wide blast radius.
 *
 * The three D2 migration routes DO share a prefix:
 *
 *   /migration   /migration-approvals   /migration-preview
 *
 * With pathname-derived scopes, a Company User holding only 'migration-preview' satisfied
 * page 'migration' for BOTH read and write -- so it could submit the irreversible, org-wide
 * migration request -- and any holder of 'migration' automatically got '/migration-approvals',
 * making that scope useless as a restriction.
 *
 * The fix keeps the URLs exactly as they are and stops these three routes relying on
 * pathname derivation: each maps to an explicit, mutually non-prefixing scope granted
 * separately in `src/mocks/data/accessControl.ts`. Every other route is untouched -- an
 * unmapped path returns `''`, which `<AccessControl>` treats as "no explicit scope" and
 * falls back to the pathname exactly as before.
 *
 * Passing a scope also publishes it on the ScopeContext, so the mutation hooks'
 * `useCheckAccess(true)` self-guard resolves against the same scope rather than the raw
 * pathname -- the route gate and the per-mutation gate therefore always agree.
 *
 * The backend remains authoritative for every actual decision; this client gate only avoids
 * pointless round-trips and hides controls that would 403.
 */
export const ROUTE_SCOPE_OVERRIDES: Record<string, string> = {
  migration: 'd2-migration',
};

/**
 * The explicit scope for a pathname, or `''` when the route has no override and should keep
 * deriving its scope from the pathname. Matches on the FIRST path segment so nested routes
 * (`/migration/foo`) resolve to the same scope as their parent.
 */
export const getRouteScope = (pathname: string): string => {
  const [firstSegment = ''] = pathname.replace(/^\//, '').split('/');
  return ROUTE_SCOPE_OVERRIDES[firstSegment] ?? '';
};
